import type { User } from "./types";

function hasAny(user: User | null, codes: string[]): boolean {
    if (!user?.isActive) {
        return false;
    }
    if (String(user.role ?? "").trim().toLowerCase() === "administrator") {
        return true;
    }
    return user.permissions.some((code) => codes.includes(code));
}

/** Hyphenated tab id → permission segment (`last-sale` → `last_sale`). */
export function tabCodeSegment(tabId: string): string {
    return tabId.replace(/-/g, "_");
}

export function screenTabViewCode(prefix: string, tabId: string): string {
    return `${prefix}.${tabCodeSegment(tabId)}.view`;
}

export function screenTabManageCode(prefix: string, tabId: string): string {
    return `${prefix}.${tabCodeSegment(tabId)}.manage`;
}

export function allScreenTabCodes(prefix: string, tabIds: readonly string[]): string[] {
    return tabIds.flatMap((tabId) => [
        screenTabViewCode(prefix, tabId),
        screenTabManageCode(prefix, tabId),
    ]);
}

/** Път / API: tab кодове + legacy screen + опционални bypass. */
export function screenRouteAccessCodes(
    prefix: string,
    tabIds: readonly string[],
    extra: readonly string[] = [],
): string[] {
    return [
        ...allScreenTabCodes(prefix, tabIds),
        `${prefix}.view`,
        `${prefix}.manage`,
        ...extra,
    ];
}

/**
 * Таб достъп: собствен код; legacy screen само ако още няма нито един tab код.
 */
export function canAccessScreenTab(
    user: User | null,
    prefix: string,
    tabId: string,
    tabIds: readonly string[],
): boolean {
    if (hasAny(user, [screenTabViewCode(prefix, tabId), screenTabManageCode(prefix, tabId)])) {
        return true;
    }
    if (hasAny(user, allScreenTabCodes(prefix, tabIds))) {
        return false;
    }
    return hasAny(user, [`${prefix}.view`, `${prefix}.manage`]);
}

export function allowedScreenTabs<T extends string>(
    user: User | null,
    prefix: string,
    tabIds: readonly T[],
): T[] {
    return tabIds.filter((tabId) => canAccessScreenTab(user, prefix, tabId, tabIds));
}

export function resolveAllowedScreenTab<T extends string>(
    user: User | null,
    prefix: string,
    tabIds: readonly T[],
    requested: T,
): T {
    const allowed = allowedScreenTabs(user, prefix, tabIds);
    if (allowed.length === 0 || allowed.includes(requested)) {
        return requested;
    }
    return allowed[0];
}

/** OR през няколко prefix-а (споделен UI: retail/b2b клиенти, b2b/warehouse поръчки). */
export function canAccessAnyPrefixTab(
    user: User | null,
    prefixes: readonly string[],
    tabId: string,
    tabIds: readonly string[],
): boolean {
    return prefixes.some((prefix) => canAccessScreenTab(user, prefix, tabId, tabIds));
}

export function allowedAnyPrefixTabs<T extends string>(
    user: User | null,
    prefixes: readonly string[],
    tabIds: readonly T[],
): T[] {
    return tabIds.filter((tabId) => canAccessAnyPrefixTab(user, prefixes, tabId, tabIds));
}

export function resolveAllowedAnyPrefixTab<T extends string>(
    user: User | null,
    prefixes: readonly string[],
    tabIds: readonly T[],
    requested: T,
): T {
    const allowed = allowedAnyPrefixTabs(user, prefixes, tabIds);
    if (allowed.length === 0 || allowed.includes(requested)) {
        return requested;
    }
    return allowed[0];
}

/** Таб id-та по screen prefix (синхрон с navScreenTabs). */
export const RETAIL_FREQUENCY_TABS = ["purchase", "hourly", "daily", "payment"] as const;
export const LOYALTY_TABS = ["registry", "detail", "actions", "rules"] as const;
export const CLIENTS_TABS = ["profiles", "workflow"] as const;
export const SUPPLIERS_TABS = ["cockpit", "registry", "buyers", "reports", "motion_to_cod", "goods_motion", "our_brand"] as const;
export const CAMPAIGNS_TABS = ["campaigns", "audiences", "templates"] as const;
export const ORDERS_TABS = ["workspace", "status"] as const;

export const RETAIL_FREQUENCY_PREFIX = "retail.frequency";
export const LOYALTY_PREFIX = "loyalty";
export const RETAIL_CLIENTS_PREFIX = "retail.clients";
export const B2B_CLIENTS_PREFIX = "b2b.clients";
export const B2B_SUPPLIERS_PREFIX = "b2b.suppliers";
export const CAMPAIGNS_PREFIX = "campaigns";
export const B2B_ORDERS_PREFIX = "b2b.orders";
export const WAREHOUSE_ORDERS_PREFIX = "warehouse.orders";
